import 'css/src/components/common/video-preview/VideoWithScrubber';

import type { RefObject } from 'react';
import React from 'react';
import type { ConnectedProps } from 'react-redux';
import type Immutable from 'immutable';
import debounce from 'lodash/debounce';
import type Luminary from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import type { SeekMode } from '@lumen5/luminary/enums';

import GenericBasePreviewElement from 'js/src/components/common/video-preview/GenericBasePreviewElement';
import SlideMarkerScrubber from 'js/src/components/common/video-preview/SlideMarkerScrubber';
import { PLAYBACK_STATE } from 'js/src/components/common/video-preview/VideoPreview';
import { enqueueTask, TASK_NAMES } from 'js/src/libs/luminary-task-queue';
import type { AppDispatch } from 'js/src/libs/RootState';
import { sentryCanvasSnapshot } from 'js/src/libs/sentry';
import Timer from 'js/src/libs/timer';
import { getTimeStringFromSeconds } from 'js/src/libs/video-duration-tools';
import {
    luminaryReduxConnect,
    slideEndTimes,
    slideIdsByStartTime,
    slideStartTimes,
} from 'js/src/luminary';
import type { CacheRef, GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

type LuminaryVideoWithScrubberOwnProps = {
    autoplay?: boolean;
    onSeek?: (newTime: number) => void;
    numOpenCommentsPerScene?: Immutable.Map<string, number>;
    commentMarkerCallback?: (time: number, forceCommentingMode: boolean) => void;
    playbackState: PLAYBACK_STATE;
    setPlaybackState: (newPlaybackState: PLAYBACK_STATE, callback?: () => void) => void;
    muted: boolean;
    onCurrentTimeUpdate?: (currentTime: number) => void;
};
type LuminaryVideoWithScrubberPropsFromRedux = ConnectedProps<
    typeof LuminaryVideoWithScrubberConnector
>;

type LuminaryVideoWithScrubberState = {
    current_time: number;
    current_transition_duration: number;
    showCommentMarker: boolean;
    readyToPlay: boolean;
};

export class LuminaryVideoWithScrubberPresentation extends GenericBasePreviewElement<
    LuminaryVideoWithScrubberOwnProps & LuminaryVideoWithScrubberPropsFromRedux,
    LuminaryVideoWithScrubberState
> {
    luminaryPreviewRef: RefObject<HTMLDivElement>;
    progressBarRef: RefObject<HTMLSpanElement>;
    _clock_timeout: Timer | null;
    PAUSE_BEFORE_END_MS = 150;

    constructor(
        props: LuminaryVideoWithScrubberOwnProps & LuminaryVideoWithScrubberPropsFromRedux,
    ) {
        super(props);

        // react refs
        this.luminaryPreviewRef = React.createRef();
        this.progressBarRef = React.createRef();

        this._clock_timeout = null;
        this.state = {
            // TODO: we can get current_time from luminary,
            // rather than having a separate piece of react state.
            current_time: 0,
            current_transition_duration: 0,
            showCommentMarker: false,
            readyToPlay: false,
        };

        // function binding
        this.pause = this.pause.bind(this);
        this.seek = this.seek.bind(this);
        this.luminaryOnPlayComplete = this.luminaryOnPlayComplete.bind(this);
    }

    override componentDidMount() {
        this.props.luminaryOn('playComplete', this.luminaryOnPlayComplete);
        this.props.luminaryOn('onRenderComplete', this.props.sendCanvasSnapshot);

        const seekToStartTask = () => {
            return new Promise<void>((resolve, reject) => {
                this._push_promise(this.props.luminaryStartDrawingToCanvas())
                    .then(() => {
                        this.props.luminaryStop();
                        if (this.props.luminaryCanPlay) {
                            this.props.luminarySeek(0);
                        }
                        this.setState({ readyToPlay: true });
                        if (!this.props.autoplay) {
                            this.init();
                        }
                        resolve();
                    })
                    .catch(() => {
                        resolve();
                    });
            });
        };
        enqueueTask({
            callback: seekToStartTask,
            taskName: TASK_NAMES.VIDEO_PREVIEW_SEEK_TO_START,
        });
        this.props.luminarySetMuted(this.props.muted);
    }

    override componentWillUnmount() {
        super.componentWillUnmount();
        // interrupt & cancel any animations, clocks, etc
        if (this._clock_timeout !== null) {
            this._clock_timeout.cancel();
            this._clock_timeout = null;
        }
        this.props.luminaryOff('pause', this.pause);
        this.props.luminaryOff('playComplete', this.luminaryOnPlayComplete);
        this.props.luminaryOff('onRenderComplete', this.props.sendCanvasSnapshot);
        this.props.luminaryStop();
        this.props.luminaryStopDrawingToCanvas();
    }

    override componentDidUpdate(
        prevProps: LuminaryVideoWithScrubberOwnProps & LuminaryVideoWithScrubberPropsFromRedux,
    ) {
        if (this.props.muted !== prevProps.muted) {
            this.props.luminarySetMuted(this.props.muted);
        }
    }

    async _setCurrentTime(current_time: number) {
        return new Promise<void>((resolve) => {
            this.setState(
                {
                    current_time,
                },
                () => {
                    if (typeof this.props.onCurrentTimeUpdate === 'function') {
                        this.props.onCurrentTimeUpdate(current_time);
                    }
                    resolve();
                },
            );
        });
    }

    _setTimer() {
        const UPDATE_INTERVAL = 100;
        this._clock_timeout = new Timer(() => {
            const currentTime = this.props.currentTime();
            this._setCurrentTime(this.props.currentTime()); // ticker in luminary is slightly faster than setTimeout

            // To achieve the "replay" state we want to check if we're very close to the end
            // If we are, pause early to preserve last visible frame!
            const timeUntilEnd = this.props.duration - currentTime;
            if (timeUntilEnd <= this.PAUSE_BEFORE_END_MS && timeUntilEnd > 0) {
                this.props.luminaryPause();
                this.pauseClock();
                this._setCurrentTime(this.props.duration);
                this.props.setPlaybackState(PLAYBACK_STATE.STOPPED);
                return;
            }

            if (this.props.playbackState === PLAYBACK_STATE.PLAYING) {
                this._setTimer();
            }
        }, UPDATE_INTERVAL);
    }

    playClock() {
        if (this._clock_timeout) {
            this._clock_timeout.resume();
        } else {
            this._setTimer();
        }
    }

    stopClock() {
        if (this._clock_timeout !== null) {
            this._clock_timeout.cancel();
            this._clock_timeout = null;
        }
        this._setCurrentTime(0);
    }

    pauseClock() {
        if (this._clock_timeout !== null) {
            this._clock_timeout.pause();
        }
    }

    getProgressBarPercentage(total_runtime: number) {
        const percentage = (this.state.current_time * 100.0) / total_runtime;
        return percentage;
    }

    /**
     * updates the progress bar for a seeked time
     * @private
     */
    _instantSeekProgressBar(new_time: number, transition_duration = 0) {
        let old_transition_duration = '100ms';
        if (this.progressBarRef.current) {
            old_transition_duration = window.getComputedStyle(
                this.progressBarRef.current,
            ).transitionDuration;
            this.progressBarRef.current.style.transitionDuration = '0s';
        }
        this._setCurrentTime(new_time);
        this.setState(
            {
                current_transition_duration: transition_duration,
            },
            () => {
                if (this.progressBarRef.current) {
                    this.progressBarRef.current.style.transitionDuration = old_transition_duration;
                }
                if (this.props.playbackState === PLAYBACK_STATE.PLAYING) {
                    this.playClock();
                }
            },
        );
    }

    seek = debounce(
        (newTime: number, transition_duration = 0) => {
            this._instantSeekProgressBar(newTime, transition_duration);
            this._push_promise(
                this.props.luminarySeek(newTime, VideoData.SeekMode.ResolveWhenCanPlay),
            );

            typeof this.props.onSeek === 'function' && this.props.onSeek(newTime);
        },
        250,
        { leading: true, trailing: true },
    );

    luminaryOnPlayComplete() {
        // This event fires after playback completes, but we handle the transition
        // early in _setTimer to preserve the last visible frame, so this is a no-op unless
        // the playback state isn't STOPPED
        if (this.props.playbackState !== PLAYBACK_STATE.STOPPED) {
            this.pauseClock();
            this._setCurrentTime(this.props.duration);
            this.props.setPlaybackState(PLAYBACK_STATE.STOPPED);
        }
    }

    /* ANIMATION METHODS */
    override play(opts = { firstPlay: true }) {
        /* can be called by parent */
        return new Promise<void>((resolve) => {
            if (!this.state.readyToPlay) {
                // try again in a little bit
                window.setTimeout(() => {
                    this.play(opts);
                }, 100);
                return;
            }

            // Check first if video resources have been loaded first
            if (!this.props.luminaryCanPlay) {
                this.props.setPlaybackState(PLAYBACK_STATE.LOADING);
                this._push_promise(this.props.luminaryOnCanPlay()).then(() => {
                    this.play({ firstPlay: true });
                });
                return;
            }

            const playFromStart =
                opts.firstPlay ||
                this.props.playbackState === PLAYBACK_STATE.STOPPED ||
                (!this.props.autoplay && this.state.current_time === 0); // edge case to avoid video starting at outro slide

            this.props.setPlaybackState(PLAYBACK_STATE.PLAYING, () => {
                this.props.luminarySetCanvasParent(this.luminaryPreviewRef.current);
                const startTime = playFromStart ? 0 : undefined;
                this.props.luminaryPlay({ startTime });
                this.props.luminaryOnce('playStart', () => {
                    this.playClock();
                    resolve();
                });
                this.props.luminaryOff('pause', this.pause);
                this.props.luminaryOnce('pause', this.pause);
            });
        });
    }

    override pause() {
        /* can be called by parent */
        return new Promise<void>((resolve) => {
            if (this.props.playbackState === PLAYBACK_STATE.LOADING) {
                resolve();
                return;
            }
            this.props.luminaryOff('pause', this.pause);
            this.props.setPlaybackState(PLAYBACK_STATE.PAUSED, () => {
                this.pauseClock();
                this.props.luminaryPause();
                resolve();
            });
        });
    }

    override stop() {
        /* can be called by parent */
        return new Promise<void>((resolve) => {
            this._setCurrentTime(0).then(() => {
                this.props.setPlaybackState(PLAYBACK_STATE.STOPPED, () => {
                    this.stopClock();
                    this.props.luminaryStop();
                    resolve();
                });
            });
        });
    }

    init() {
        if (!this.state.readyToPlay) {
            // try again in a little bit
            window.setTimeout(() => {
                this.init();
            }, 100);
            return;
        }

        // Check first if video resources have been loaded first
        if (!this.props.luminaryCanPlay) {
            this.props.setPlaybackState(PLAYBACK_STATE.LOADING);
            this._push_promise(this.props.luminaryOnCanPlay()).then(() => {
                this.init();
            });
            return;
        }

        this.props.setPlaybackState(PLAYBACK_STATE.PAUSED, () => {
            this.props.luminarySetCanvasParent(this.luminaryPreviewRef.current);

            // if we are starting from a paused state, seek to the middle of the first slide
            // This is like showing a `poster` of the video.
            const middleOfFirstSlide =
                this.props.slideStartTimes[0] + this.props.firstSceneDuration / 2;
            this.props.luminarySeek(middleOfFirstSlide);
        });
    }

    override render() {
        let current_slide;
        let slide_markers = null;
        const total_runtime = this.props.duration;
        const aspect_ratio = this.props.aspectRatio;

        switch (this.props.playbackState) {
            case PLAYBACK_STATE.LOADING:
                current_slide = (
                    <div className="pe-none">
                        <div
                            className={`luminary-canvas top-corner-radius text-center ${aspect_ratio}`}
                        >
                            <div className="d-flex position-absolute w-100 h-100">
                                <span className="m-l5-auto">Loading...</span>
                            </div>
                        </div>
                    </div>
                );
                break;

            case PLAYBACK_STATE.PLAYING:
            case PLAYBACK_STATE.PAUSED:
            case PLAYBACK_STATE.STOPPED: {
                current_slide = (
                    <div className={`luminary-canvas top-corner-radius pe-none ${aspect_ratio}`}>
                        <div className="luminary-preview" ref={this.luminaryPreviewRef} />
                    </div>
                );
                slide_markers = (
                    <SlideMarkerScrubber
                        slideStartTimes={this.props.slideStartTimes}
                        slideEndTimes={this.props.slideEndTimes}
                        slideIdsByStartTime={this.props.slideIdsByStartTime}
                        numOpenCommentsPerScene={this.props.numOpenCommentsPerScene}
                        duration={this.props.duration}
                        seek={this.seek}
                        commentMarkerCallback={this.props.commentMarkerCallback}
                    />
                );
                break;
            }
            default:
                break;
        }

        return (
            <div className="col-md-12 luminary-video-with-scrubber">
                <div className="slide-corner-radius slide-shadow slide-wrapper">
                    {current_slide}
                    <div className="d-flex align-items-center slide-meta">
                        <div className="progress slide-meta-overlay">
                            <span
                                ref={this.progressBarRef}
                                className="progress-bar progress-bar-grey progress-bar-second-transitions w-100"
                                style={{
                                    transform: `scaleX(${this.getProgressBarPercentage(total_runtime) / 100})`,
                                }}
                            />
                        </div>
                        {slide_markers}
                        <div className="position-absolute start-0 ms-l5-8 pe-none blur-text-bkg">
                            {getTimeStringFromSeconds(this.state.current_time / 1000)}
                        </div>
                        <div className="position-absolute end-0 me-l5-8 pe-none blur-text-bkg">
                            {getTimeStringFromSeconds(total_runtime / 1000)}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const ASL_mapLuminaryStateToProps = (
    luminary: Luminary,
    ownProps: LuminaryVideoWithScrubberOwnProps,
    cacheRef: CacheRef,
) => {
    return {
        luminaryCanPlay: luminary.canPlay,
        duration: luminary.duration,
        aspectRatio: luminary.aspectRatio,
        firstSceneDuration: luminary.scenes.length > 1 ? luminary.scenes[0].duration : 0,
        slideStartTimes: cacheRef('slideStartTimes', slideStartTimes(luminary)),
        slideEndTimes: cacheRef('slideEndTimes', slideEndTimes(luminary)),
        currentTime: cacheRef('currentTime', () => luminary.currentTime),
        slideIdsByStartTime: cacheRef('slideIdsByStartTime', slideIdsByStartTime(luminary)),
    };
};
const ASL_mapLuminaryOperationsToProps = (
    dispatch: AppDispatch,
    getState: GetRootState,
    luminary: Luminary,
) => {
    return {
        // hacked-in event handlers - not ideal, but workable for now
        luminaryOn: luminary.on.bind(luminary),
        luminaryOff: luminary.off.bind(luminary),
        luminaryOnce: luminary.once.bind(luminary),
        luminaryOnCanPlay: luminary.onCanPlay.bind(luminary),
        luminarySeek: async (seekTime: number, seekMode?: SeekMode) => {
            await luminary.seek(seekTime, seekMode);
            if (!luminary.canvas) {
                return;
            }
            await sentryCanvasSnapshot(luminary.canvas);
        },
        luminarySetCanvasParent: (canvasParent: HTMLDivElement | null) => {
            luminary.canvasParent = canvasParent;
        },
        luminaryStartDrawingToCanvas: luminary.startDrawingToCanvas.bind(luminary),
        luminaryPlay: luminary.play.bind(luminary),
        luminaryPause: luminary.pause.bind(luminary),
        luminaryStop: luminary.stop.bind(luminary),
        luminaryStopDrawingToCanvas: luminary.stopDrawingToCanvas.bind(luminary),
        luminarySetMuted: (muted: boolean) => {
            luminary.muted = muted;
        },
        sendCanvasSnapshot: async () => {
            if (luminary.isPlaying() && luminary.canvas) {
                await sentryCanvasSnapshot(luminary.canvas);
            }
        },
    };
};
const LuminaryVideoWithScrubberConnector = luminaryReduxConnect(
    undefined,
    undefined,
    ASL_mapLuminaryStateToProps,
    ASL_mapLuminaryOperationsToProps,
    {
        forwardRef: true,
        eventsToSubscribeTo: ['stateChanged', 'onCanPlay'],
    },
);
const LuminaryVideoWithScrubber = LuminaryVideoWithScrubberConnector(
    LuminaryVideoWithScrubberPresentation,
);

export default LuminaryVideoWithScrubber;
